import { useCallback, useEffect, useState } from 'react'
import { Fa, showErrorMessage, Table } from '@eltoro-ui/components'
import { useTokenPagination } from '@eltoro-ui/hooks'
import { V1Subscription, V1PublisherSubscription } from 'next-gen-sdk'
import { Link, useNavigate } from 'react-router-dom'
import { TablePagination } from 'Components'
import { useAppContext } from 'Contexts'
import { listOrgSubscriptions } from '../../../Requests'
import { SearchBar } from './SearchBar'
import { getSubscriptionTableColumns } from './getSubscriptionTableColumns'
import { useTableSearch } from './useTableSearch'

export type PopulatedSubscriptionRow = V1Subscription &
  V1PublisherSubscription & {
    loading: boolean
    pixelName?: string
  }

export const checkIfPublisherSubscription = (
  item: V1Subscription | V1PublisherSubscription,
): item is V1PublisherSubscription => {
  return !!(item as V1PublisherSubscription).pixelName
}

export const SubscriptionTable = (props: {
  refresh: string
  partner?: boolean
}) => {
  const [populatedRows, setPopulatedRows] = useState<
    PopulatedSubscriptionRow[]
  >([])
  const { webToHomeApi, webToHomePrintPartnerApi, currentOrg } = useAppContext()
  const nav = useNavigate()
  const tableSearchProps = useTableSearch()
  const query = currentOrg?.id ? `?org_id=${currentOrg.id}` : ''
  const {
    sort,
    setSort,
    debouncedSearchInput,
    searchType,
    processedSearchInput,
  } = tableSearchProps

  const orderBy =
    sort && `${sort.field}${sort.direction === 'desc' ? ' desc' : ''}`

  const handleSort = (
    _: number,
    sort?: 'asc' | 'desc',
    path?: string | string[],
  ) =>
    !sort || !path
      ? setSort(undefined)
      : setSort({ field: path as string, direction: sort || 'asc' })

  const { partner, refresh } = props

  const handleFetchSubscriptions = useCallback(
    async ({
      pageSize,
      nextPageToken,
    }: {
      pageSize: number
      nextPageToken?: string
    }) => {
      const empty = {
        data: [],
        totalItems: 0,
      }

      if (!currentOrg?.id) return empty
      setPopulatedRows([])
      try {
        const res = partner
          ? await webToHomePrintPartnerApi?.advertisingPlatformServiceListPublisherSubscriptions(
              currentOrg.id,
              pageSize,
              nextPageToken,
              currentOrg.id,
              orderBy || 'created desc', // newest -> oldest
              searchType === 'name' ? debouncedSearchInput : undefined, // name
              searchType === 'id' ? debouncedSearchInput : undefined, // id
            )
          : await listOrgSubscriptions(
              pageSize,
              nextPageToken,
              currentOrg.id,
              orderBy || 'created desc', // newest -> oldest
              searchType === 'id' ? debouncedSearchInput : undefined, // id
              searchType === 'name' ? debouncedSearchInput : undefined, // name
              undefined, // pixelId - not used in search
              undefined, // pixelName - not used in search
              searchType === 'tags' &&
                (processedSearchInput as string[]).length > 0
                ? (processedSearchInput as string[])
                : undefined, // tags
            )
        if (res) {
          return {
            data: (res.subscriptions || []).map(
              (sub: V1Subscription | V1PublisherSubscription) =>
                ({ ...sub, loading: true } as PopulatedSubscriptionRow),
            ),
            totalItems: res.totalSize || 0,
            nextPageToken: res.nextPageToken,
          }
        }
      } catch {
        showErrorMessage('Error fetching your subscriptions', '')
      }

      return empty
    },
    [
      currentOrg?.id,
      partner,
      debouncedSearchInput,
      searchType,
      orderBy,
      processedSearchInput,
      webToHomePrintPartnerApi,
    ],
  )

  const pagination = useTokenPagination<PopulatedSubscriptionRow>(
    {
      pageSize: 10,
      fetchData: handleFetchSubscriptions,
    },
    [currentOrg, refresh, partner, orderBy, debouncedSearchInput, searchType],
  )

  useEffect(() => {
    let ignore = false
    // populate rows
    const populateRows = async (
      subscriptions: (V1Subscription | V1PublisherSubscription)[],
    ) => {
      if (currentOrg?.id) {
        const completedRows = await Promise.all(
          subscriptions.map(async (subscription) => {
            if (!currentOrg?.id || !webToHomeApi || !subscription.id)
              return Promise.resolve(subscription)
            let pixelName = 'N/A'

            const isPublisher = checkIfPublisherSubscription(subscription)

            if (isPublisher) {
              // TypeScript now knows this is V1PublisherSubscription
              const { pixelName: publisherPixelName } = subscription
              if (publisherPixelName) {
                pixelName = publisherPixelName
              }
            } else {
              // TypeScript now knows this is V1Subscription
              const regularSubscription = subscription as V1Subscription
              if (regularSubscription.pixelId && regularSubscription.orgId) {
                const fullPixel = await webToHomeApi
                  ?.advertisingPlatformServiceGetPixel(
                    regularSubscription.pixelId,
                    regularSubscription.orgId,
                  )
                  .catch((e) => {
                    // if not found, it could be a hidden pixel. skip showing error msg
                    if (e.code !== 404) {
                      showErrorMessage('Error fetching pixel', '')
                    }
                    return undefined
                  })
                if (fullPixel?.name) {
                  pixelName = fullPixel.name
                }
              }
            }

            return {
              ...subscription,
              pixelName,
              loading: false,
            } as PopulatedSubscriptionRow
          }),
        )
        return completedRows as PopulatedSubscriptionRow[]
      }
      return [] as PopulatedSubscriptionRow[]
    }

    if (pagination.currentPageData) {
      populateRows(pagination.currentPageData).then((newRows) => {
        if (!ignore) setPopulatedRows(newRows)
      })
    }
    return () => {
      ignore = true
    }
  }, [pagination.currentPageData, currentOrg?.id, webToHomeApi])

  const columns = getSubscriptionTableColumns(partner)

  return (
    <div className="SubscriptionTable">
      <SearchBar
        type={`${partner ? 'partner ' : ''}subscriptions`}
        {...tableSearchProps}
      />
      <div className={pagination.loading ? 'animate-pulse opacity-50' : ''}>
        <Table
          className="SubscriptionTable__table"
          columns={columns}
          rows={
            populatedRows.length > 0
              ? populatedRows
              : pagination.currentPageData
          }
          // onSort={() => {}}
          onClick={(row) =>
            (row as V1Subscription).pixelId && !partner
              ? nav(
                  `pixel/${(row as V1Subscription).pixelId}/subscription/${
                    row.id
                  }${query}`,
                )
              : nav(
                  `publisher/${row.publisherId}/subscription/${row.id}${query}`,
                )
          }
          onSort={handleSort}
        />
      </div>
      {!pagination.loading && !pagination.totalItems ? (
        <div className="PixelLibraryPage__empty-state flex flex-col items-center py-6">
          <Fa
            icon="folder"
            size={5}
            type="duotone"
            className="text-primary-200"
          />
          <p className="text-grey-600">
            Your Pixel Library is empty.
            <Link className="LibraryTableLink pl-1" to="/audiences/create">
              Create some now!
            </Link>
          </p>
        </div>
      ) : null}
      {pagination.totalItems > 10 ? (
        <div className="PixelLibraryPage__pagination-footer mb-2">
          <TablePagination pagination={pagination} />
        </div>
      ) : null}
    </div>
  )
}
